/**
 * Membership Gates Utility
 * Frontend utility for checking membership permissions before API calls
 * and displaying appropriate UI states
 */

// Plan hierarchy for comparison
const PLAN_HIERARCHY = {
  'basic': 1,
  'gcc-business': 2,
  'global-enterprise': 3,
  'admin': 999
};

// GCC Countries for region-based checks
export const GCC_COUNTRIES = ['AE', 'SA', 'KW', 'QA', 'BH', 'OM'];
export const GCC_COUNTRY_NAMES = [
  'United Arab Emirates', 'UAE',
  'Saudi Arabia', 'KSA',
  'Kuwait',
  'Qatar',
  'Bahrain',
  'Oman'
];

/**
 * Check if user can upload more products
 * @param {number} currentCount - Current number of products
 * @param {number} limit - Plan's product limit
 * @returns {boolean}
 */
export const canUploadProduct = (currentCount, limit) => {
  return currentCount < limit;
};

/**
 * Get remaining products count
 * @param {number} currentCount - Current number of products
 * @param {number} limit - Plan's product limit
 * @returns {number}
 */
export const getRemainingProducts = (currentCount, limit) => {
  return Math.max(0, limit - currentCount);
};

/**
 * Check if user has access to a specific feature
 * @param {string} featureName - Feature key
 * @param {object} planFeatures - User's plan features object
 * @returns {boolean}
 */
export const canAccessFeature = (featureName, planFeatures) => {
  if (!planFeatures || !planFeatures.features) {
    return false;
  }
  return !!planFeatures.features[featureName];
};

/**
 * Get feature value (for non-boolean features like limits)
 * @param {string} featureName - Feature key
 * @param {object} planFeatures - User's plan features object
 * @param {any} defaultValue - Default value if feature not found
 * @returns {any}
 */
export const getFeatureValue = (featureName, planFeatures, defaultValue = null) => {
  if (!planFeatures || !planFeatures.features) {
    return defaultValue;
  }
  return planFeatures.features[featureName] ?? defaultValue;
};

/**
 * Check if user can upload more images to a product
 * @param {number} currentImageCount - Current images in product
 * @param {object} planFeatures - User's plan features
 * @returns {boolean}
 */
export const canUploadMoreImages = (currentImageCount, planFeatures) => {
  const limit = getFeatureValue('maxProductImages', planFeatures, 5);
  return currentImageCount < limit;
};

/**
 * Get remaining image slots
 * @param {number} currentImageCount - Current images in product
 * @param {object} planFeatures - User's plan features
 * @returns {number}
 */
export const getRemainingImageSlots = (currentImageCount, planFeatures) => {
  const limit = getFeatureValue('maxProductImages', planFeatures, 5);
  return Math.max(0, limit - currentImageCount);
};

/**
 * Check if country is in GCC region
 * @param {string} country - Country code or name
 * @returns {boolean}
 */
export const isGCCCountry = (country) => {
  if (!country) return false;
  
  const upperCountry = country.toUpperCase();
  const lowerCountry = country.toLowerCase();
  
  if (GCC_COUNTRIES.includes(upperCountry)) return true;
  
  return GCC_COUNTRY_NAMES.some(name => 
    name.toLowerCase() === lowerCountry || 
    lowerCountry.includes(name.toLowerCase())
  );
};

/**
 * Check if user can view buyer contact based on region
 * @param {string} buyerCountry - Buyer's country
 * @param {object} planFeatures - User's plan features
 * @returns {boolean}
 */
export const canViewBuyerContact = (buyerCountry, planFeatures) => {
  if (!canAccessFeature('canViewBuyerContacts', planFeatures)) {
    return false;
  }
  
  const region = getFeatureValue('buyerContactRegion', planFeatures);
  
  if (!region || region === 'all') {
    return true;
  }
  
  if (region === 'gcc') {
    return isGCCCountry(buyerCountry);
  }
  
  return false;
};

/**
 * Get upgrade message for a blocked feature
 * @param {string} featureName - Feature key
 * @returns {string}
 */
export const getUpgradeMessage = (featureName) => {
  const messages = {
    productPostingLimit: 'Upgrade your plan to list more products',
    maxProductImages: 'Upgrade your plan to add more images per product',
    canViewBuyerContactDetails: 'Upgrade to GCC Business or Global Enterprise to view buyer contact details',
    canViewBuyerContacts: 'Upgrade to GCC Business or Global Enterprise to view buyer contact details',
    canAccessBuyerDirectory: 'Upgrade to GCC Business or Global Enterprise to access the buyer directory',
    canUploadVideos: 'Upgrade to GCC Business or Global Enterprise to upload videos to your profile',
    canLiveChat: 'Upgrade to GCC Business or Global Enterprise to use live chat',
    canHighlightBrand: 'Upgrade to GCC Business or Global Enterprise to highlight your brand',
    canAddToProfessionals: 'Upgrade to GCC Business or Global Enterprise to be listed as a professional',
    canAddProfessional: 'Upgrade to GCC Business or Global Enterprise to be listed as a professional',
    canSubmitProjects: 'Upgrade to GCC Business or Global Enterprise to submit projects',
    canPostNews: 'Upgrade to GCC Business or Global Enterprise to post news articles',
    canReceiveDirectCalls: 'Upgrade to Global Enterprise to receive direct calls and view mobile numbers from buyers'
  };
  
  return messages[featureName] || 'Upgrade your plan to access this feature';
};

/**
 * Get plan display name from slug
 * @param {string} planSlug - Plan slug
 * @returns {string}
 */
export const getPlanDisplayName = (planSlug) => {
  const names = {
    'basic': 'Basic',
    'gcc-business': 'GCC Business',
    'global-enterprise': 'Global Enterprise',
    'admin': 'Admin'
  };
  return names[planSlug?.toLowerCase()] || planSlug || 'Basic';
};

/**
 * Compare two plans
 * @param {string} planA - First plan slug
 * @param {string} planB - Second plan slug
 * @returns {number} -1 if A < B, 0 if equal, 1 if A > B
 */
export const comparePlans = (planA, planB) => {
  const levelA = PLAN_HIERARCHY[planA?.toLowerCase()] || 0;
  const levelB = PLAN_HIERARCHY[planB?.toLowerCase()] || 0;
  
  if (levelA < levelB) return -1;
  if (levelA > levelB) return 1;
  return 0;
};

/**
 * Check if user's plan is at least a certain level
 * @param {string} currentPlan - User's current plan
 * @param {string} requiredPlan - Minimum required plan
 * @returns {boolean}
 */
export const isPlanAtLeast = (currentPlan, requiredPlan) => {
  return comparePlans(currentPlan, requiredPlan) >= 0;
};

/**
 * Get features list with availability status for a plan
 * @param {object} planFeatures - User's plan features
 * @returns {Array} Features with name, available status, and upgrade info
 */
export const getFeaturesList = (planFeatures) => {
  const featureKeys = [
    { key: 'canReceiveRfqs', name: 'Receive RFQs / Buy Leads', category: 'leads' },
    { key: 'canViewBuyerContacts', name: 'View Buyer Contact Details', category: 'leads' },
    { key: 'canAccessBuyerDirectory', name: 'Access Buyer Directory', category: 'leads' },
    { key: 'canReceiveDirectCalls', name: 'Receive Direct Calls', category: 'communication' },
    { key: 'canLiveChat', name: 'Live Chat', category: 'communication' },
    { key: 'canUploadVideos', name: 'Upload Videos', category: 'profile' },
    { key: 'canHighlightBrand', name: 'Brand Spotlight', category: 'visibility' },
    { key: 'canAddProfessional', name: 'Professional Listing', category: 'visibility' },
    { key: 'canSubmitProjects', name: 'Submit Projects', category: 'content' },
    { key: 'canPostNews', name: 'Post News Articles', category: 'content' }
  ];
  
  return featureKeys.map(({ key, name, category }) => ({
    key,
    name,
    category,
    available: canAccessFeature(key, planFeatures),
    upgradeMessage: getUpgradeMessage(key)
  }));
};

/**
 * Get product limit info
 * @param {number} currentCount - Current product count
 * @param {object} planFeatures - User's plan features
 * @returns {object} Limit info with percentage, remaining, etc.
 */
export const getProductLimitInfo = (currentCount, planFeatures) => {
  const limit = getFeatureValue('productPostingLimit', planFeatures, 10);
  const remaining = Math.max(0, limit - currentCount);
  const percentage = Math.min(100, (currentCount / limit) * 100);
  const isAtLimit = currentCount >= limit;
  const isNearLimit = percentage >= 80;
  
  return {
    current: currentCount,
    limit,
    remaining,
    percentage,
    isAtLimit,
    isNearLimit,
    message: isAtLimit 
      ? `You've reached your limit of ${limit} products. Upgrade to add more.`
      : isNearLimit 
        ? `You're using ${currentCount} of ${limit} products (${remaining} remaining)`
        : `${currentCount} of ${limit} products used`
  };
};

/**
 * Check if subscription is expiring soon
 * @param {string} endDate - Subscription end date
 * @param {number} warningDays - Days before expiry to warn (default 7)
 * @returns {object} Expiry status
 */
export const checkSubscriptionExpiry = (endDate, warningDays = 7) => {
  if (!endDate) {
    return { isExpiring: false, isExpired: false, daysRemaining: null };
  }
  
  const end = new Date(endDate);
  const now = new Date();
  const diffTime = end - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return {
    isExpiring: diffDays > 0 && diffDays <= warningDays,
    isExpired: diffDays <= 0,
    daysRemaining: diffDays > 0 ? diffDays : 0,
    expiryDate: end.toLocaleDateString()
  };
};

/**
 * Format membership plan for display
 * @param {object} planData - Plan data from API
 * @returns {object} Formatted plan info
 */
export const formatPlanForDisplay = (planData) => {
  return {
    name: planData?.planDisplayName || planData?.planName || 'Basic',
    slug: planData?.planName?.toLowerCase()?.replace(/\s+/g, '-') || 'basic',
    hasSubscription: planData?.hasSubscription || false,
    features: planData?.features || {},
    limits: {
      products: planData?.features?.productPostingLimit || 10,
      images: planData?.features?.maxProductImages || 5
    }
  };
};

export default {
  canUploadProduct,
  getRemainingProducts,
  canAccessFeature,
  getFeatureValue,
  canUploadMoreImages,
  getRemainingImageSlots,
  isGCCCountry,
  canViewBuyerContact,
  getUpgradeMessage,
  getPlanDisplayName,
  comparePlans,
  isPlanAtLeast,
  getFeaturesList,
  getProductLimitInfo,
  checkSubscriptionExpiry,
  formatPlanForDisplay,
  GCC_COUNTRIES,
  GCC_COUNTRY_NAMES
};
